import useTranslation from "../../../client/hooks/useTranslation";
import { useLiteData, type Charge } from "../../data/LiteDataProvider";
import { Bar, Card } from "../../ui/ui";
import type { Breakdown, PfmEvent } from "./pfmModel";
import { EventRow } from "./shared";
import { UpcomingChargesPanel } from "../../../prop-firm-manager/upcoming/UpcomingChargesPanel";

/** Prélèvements récurrents : à confirmer, puis les 30 prochains jours. */
export function UpcomingCard({ charges }: { charges: Charge[] }) {
  const t = useTranslation().lite.pfm.upcoming;
  const { fmt, accountsById } = useLiteData();
  const accountName = (c: Charge) =>
    c.accountId ? accountsById.get(c.accountId)?.name ?? c.account?.name ?? "" : t.global;
  return (
    <Card title={t.title} subtitle={t.subtitle}>
      <UpcomingChargesPanel charges={charges} fmt={fmt} accountName={accountName} />
    </Card>
  );
}

/**
 * `fill` : la carte occupe la hauteur restante de sa colonne (à côté du
 * calendrier) et la liste défile à l'intérieur, sans pousser la rangée.
 */
export function ActivityCard({ events, fill = false }: { events: PfmEvent[]; fill?: boolean }) {
  const t = useTranslation().lite.pfm.activity;
  return (
    <Card
      title={t.title}
      pad={false}
      className={fill ? "xl:flex-1 xl:min-h-[240px] flex flex-col" : ""}
      bodyClassName={fill ? "xl:flex-1 xl:relative xl:min-h-0 xl:mt-3" : ""}
    >
      {events.length ? (
        <div className={`divide-y divide-border border-t border-border mt-3 max-h-[300px] overflow-y-auto ${fill ? "xl:max-h-none xl:absolute xl:inset-0 xl:mt-0" : ""}`}>
          {events.map(e => <EventRow key={e.id} e={e} />)}
        </div>
      ) : <p className="px-5 pb-5 pt-3 text-sm text-muted-foreground">{t.none}</p>}
    </Card>
  );
}

/** Répartition des dépenses par nature, depuis le début. */
export function ExpenseBreakdown({ b }: { b: Breakdown }) {
  const t = useTranslation().lite.pfm;
  const { fmt } = useLiteData();
  const rows = [
    { k: "challenges", label: t.recap.challenges, v: b.challenges },
    { k: "activations", label: t.recap.activations, v: b.activations },
    { k: "recurring", label: t.recap.recurring, v: b.recurring },
    { k: "oneOff", label: t.recap.oneOff, v: b.oneOff },
  ].filter(r => r.v > 0).sort((x, y) => y.v - x.v);
  return (
    <Card title={t.byType.title} subtitle={t.byType.subtitle}>
      {rows.length ? (
        <div className="space-y-4">
          {rows.map(r => (
            <div key={r.k}>
              <div className="flex justify-between gap-3 text-sm mb-1.5">
                <span>{r.label}</span>
                <span className="font-mono tabular-nums">
                  {fmt.money(-r.v)} <span className="text-muted-foreground/70 text-xs">({fmt.pct(r.v / b.total)})</span>
                </span>
              </div>
              <Bar value={r.v / b.total} tone="loss" />
            </div>
          ))}
        </div>
      ) : <p className="text-sm text-muted-foreground">{t.byType.none}</p>}
    </Card>
  );
}
